import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateLinkSessionQuery,
  useLazyGetMyselfQuery,
} from "../api/loginApi";
import { useLazyGetProjectStateQuery } from "../api/managementApi";
import OpenMeasureDialog from "../components/dialogs/OpenMeasureDialog";
import Page from "../components/Page";
import useAuth from "../hooks/useAuth";

function LinkRedirectPage() {
  const navigate = useNavigate();

  const { clientId, projectId, stateId, token } = useParams();
  const { setLinkToken, linkToken } = useAuth();

  const { data: linkSession, error: linkSessionError } =
    useCreateLinkSessionQuery(token);

  const [openMeasureDialogOpen, setOpenMeasureDialogOpen] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState(null);

  const [resultLink, setResultLink] = useState(null);
  const [getMyself, { data: myself }] = useLazyGetMyselfQuery();

  const [getProjectState] = useLazyGetProjectStateQuery();

  useEffect(() => {
    if (!linkToken) return;

    if (resultLink || selectedMeasure) {
      getMyself()
        .unwrap()
        .then((myself) => {})
        .catch((err) => {
          console.log("err", err);
        });
    }
  }, [resultLink, linkToken, selectedMeasure, token, getMyself]);

  useEffect(() => {
    if (!myself) return;

    if (resultLink) {
      navigate(resultLink);
      return;
    }

    if (selectedMeasure) {
      let timeout = setTimeout(() => {
        setOpenMeasureDialogOpen(true);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [myself, navigate, resultLink, selectedMeasure]);

  // handles project license expiration redirects
  useEffect(() => {
    if (!linkSessionError) return;

    /**
     * Returns a flat array of the inaccessible state names returned from BE
     * {name: "state1", name: "state2", ...} => ["state1", "state2", ...]
     *
     * @returns {string[]}
     */
    const flatMapOfStateNames = () =>
      // typo is on purpose since the property comes like this from the BE... 🤷🏻
      linkSessionError.data.inaccesibleStates.reduce(
        (acc, state) => acc.concat(state.name),
        [],
      );

    switch (linkSessionError.status) {
      case 401:
        navigate("/link-error", { replace: true });
        break;
      // BE returns a 403 when the license is expired
      case 403:
      default:
        if (!linkSessionError.data.inaccesibleStates) {
          navigate(`/license-expired`, { replace: true });
          return;
        }
        navigate(
          `/license-expired?titles=${encodeURIComponent(
            JSON.stringify(flatMapOfStateNames()),
          )}`,
          { replace: true },
        );
    }
  }, [linkSessionError, navigate]);

  useEffect(() => {
    if (clientId && projectId && stateId && linkSession?.token) {
      setLinkToken({
        token: linkSession.token,
        level: "state",
        accessData: { clientId, projectId, stateId },
      });

      getProjectState({
        clientId,
        projectId,
        id: stateId,
      })
        .unwrap()
        .then((projectState) => {
          setSelectedMeasure({
            ...projectState,
          });
        })
        .catch((err) => {
          navigate("/link-error", { replace: true });
        });
    } else if (clientId && projectId && linkSession?.token) {
      setLinkToken({
        token: linkSession.token,
        level: "project",
        accessData: { clientId, projectId },
      });
      setResultLink(`/clients/${clientId}/projects/${projectId}`);
    } else if (clientId && linkSession?.token) {
      setLinkToken({
        token: linkSession.token,
        level: "client",
        accessData: { clientId },
      });

      setResultLink(`/clients/${clientId}`);
    }
  }, [
    linkSession,
    clientId,
    getProjectState,
    navigate,
    projectId,
    setLinkToken,
    stateId,
  ]);

  return (
    <Page className={"h-screen flex justify-center"}>
      <OpenMeasureDialog
        disableCloseFromUI={true}
        isOpen={openMeasureDialogOpen}
        clientId={clientId}
        projectId={projectId}
        measure={selectedMeasure}
        onClose={() => {
          setSelectedMeasure(null);
          setOpenMeasureDialogOpen(false);
        }}
      />
    </Page>
  );
}

export default LinkRedirectPage;
